'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { ChevronRight, Home, FilePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getAuthHeaders } from '@/lib/api-utils'
import { CreateIssueWizardBody } from '@/components/issues/create-issue-wizard-body'
import { uploadPendingAttachments, type PendingFile } from '@/components/issues/issue-attachments'
import { cn } from '@/lib/utils'

interface CreateIssuePageProps {
  /** When set, the wizard runs in edit mode and pre-fills from this issue. */
  issueId?: string
  initialProjectId?: string
  parentIssueId?: string
}

interface ModalData {
  statuses: Array<{ id: number | string; name: string }>
  trackers: Array<{ id: number | string; name: string }>
  priorities: Array<{ id: number | string; name: string }>
  versions: Array<{ id: number | string; name: string }>
  members: Array<{ id: number | string; name: string }>
  categories: Array<{ id: number | string; name: string }>
  projects: Array<{ id: number | string; name: string }>
}

const EMPTY_FORM = {
  subject: '',
  description: '',
  project_id: '',
  tracker_id: '',
  status_id: '',
  priority_id: '',
  assigned_to_id: '',
  fixed_version_id: '',
  category_id: '',
  parent_issue_id: '',
  start_date: '',
  due_date: '',
  estimated_hours: '',
  done_ratio: '0',
  is_private: false,
  notes: '',
}

export function CreateIssuePageClient({ issueId, initialProjectId, parentIssueId }: CreateIssuePageProps) {
  const router = useRouter()
  const isEdit = !!issueId

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<{ kind: 'error' | 'success'; message: string } | null>(null)
  const [loadingData, setLoadingData] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [modalData, setModalData] = useState<ModalData | null>(null)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [enhancingDescription, setEnhancingDescription] = useState(false)

  const [formData, setFormData] = useState({
    ...EMPTY_FORM,
    project_id: initialProjectId || '',
    parent_issue_id: parentIssueId || '',
  })

  const fetchData = useCallback(async (effectiveProjectId?: string) => {
    setLoadingData(true)
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        setBanner({ kind: 'error', message: 'You must be signed in to create an issue.' })
        setLoadingData(false)
        return
      }

      const versionsPromise = effectiveProjectId
        ? fetch(`/api/projects/${effectiveProjectId}/versions`, { headers: authHeaders })
        : Promise.resolve(new Response(JSON.stringify({ versions: [] }), { status: 200 }))

      const [statusesRes, trackersRes, prioritiesRes, versionsRes, membersRes, projectsRes] = await Promise.allSettled([
        fetch('/api/issue_statuses', { headers: authHeaders }),
        fetch('/api/trackers', { headers: authHeaders }),
        fetch('/api/enumerations/issue_priorities', { headers: authHeaders }),
        versionsPromise,
        fetch('/api/users', { headers: authHeaders }),
        fetch('/api/projects?limit=100', { headers: authHeaders }),
      ])

      const data: ModalData = { statuses: [], trackers: [], priorities: [], versions: [], members: [], categories: [], projects: [] }

      if (statusesRes.status === 'fulfilled' && statusesRes.value.ok) {
        const d = await statusesRes.value.json()
        data.statuses = d.issue_statuses || []
      }
      if (trackersRes.status === 'fulfilled' && trackersRes.value.ok) {
        const d = await trackersRes.value.json()
        data.trackers = d.trackers || []
      }
      if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value.ok) {
        const d = await prioritiesRes.value.json()
        data.priorities = d.issue_priorities || []
      }
      if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
        const d = await versionsRes.value.json()
        data.versions = d.versions || []
      }
      if (membersRes.status === 'fulfilled' && membersRes.value.ok) {
        const d = await membersRes.value.json()
        const users = (d.users || []).filter((u: any) => u?.id).map((u: any) => ({
          id: u.id,
          name: u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim() || u.login || u.email || '',
        }))
        data.members = users
      }
      if (projectsRes.status === 'fulfilled' && projectsRes.value.ok) {
        const d = await projectsRes.value.json()
        data.projects = (d.projects || []).map((p: any) => ({ id: p.id, name: p.name }))
      }

      setModalData(data)
    } catch (err: any) {
      setBanner({ kind: 'error', message: err?.message || 'Failed to load form data' })
    } finally {
      setLoadingData(false)
    }
  }, [])

  // Load form lookup data
  useEffect(() => {
    fetchData(initialProjectId)
  }, [fetchData, initialProjectId])

  const handleProjectChange = useCallback(
    async (newProjectId: string) => {
      setFormData(prev => ({ ...prev, project_id: newProjectId, assigned_to_id: '', fixed_version_id: '', category_id: '' }))
      if (!newProjectId) return
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return
      try {
        const [versionsRes, categoriesRes] = await Promise.allSettled([
          fetch(`/api/projects/${newProjectId}/versions`, { headers: authHeaders }),
          fetch(`/api/projects/${newProjectId}/issue_categories`, { headers: authHeaders }),
        ])
        const next: Partial<ModalData> = {}
        if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
          const d = await versionsRes.value.json()
          next.versions = d.versions || []
        }
        if (categoriesRes.status === 'fulfilled' && categoriesRes.value.ok) {
          const d = await categoriesRes.value.json()
          next.categories = (d.issue_categories || []).map((c: any) => ({ id: c.id, name: c.name }))
        }
        setModalData(prev => (prev ? { ...prev, ...next } : prev))
      } catch {
        // soft fail — versions/categories are optional fields
      }
    },
    [],
  )

  // When editing, hydrate the form from the existing issue
  useEffect(() => {
    if (!issueId) return
    const authHeaders = getAuthHeaders()
    if (!authHeaders) return
    let cancelled = false
    fetch(`/api/issues/${issueId}`, { headers: authHeaders as Record<string, string> })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => {
        if (cancelled) return
        const i = data?.issue
        if (!i) {
          setBanner({ kind: 'error', message: 'Issue not found.' })
          return
        }
        setFormData(prev => ({
          ...prev,
          subject: i.subject || '',
          description: i.description || '',
          project_id: String(i.project?.id || ''),
          tracker_id: String(i.tracker?.id || ''),
          status_id: String(i.status?.id || ''),
          priority_id: String(i.priority?.id || ''),
          assigned_to_id: String(i.assigned_to?.id || ''),
          fixed_version_id: String(i.fixed_version?.id || ''),
          category_id: String(i.category?.id || ''),
          parent_issue_id: String(i.parent?.id || ''),
          start_date: i.start_date || '',
          due_date: i.due_date || '',
          estimated_hours: i.estimated_hours != null ? String(i.estimated_hours) : '',
          done_ratio: String(i.done_ratio ?? 0),
          is_private: !!i.is_private,
          notes: '',
        }))
        if (i.project?.id && String(i.project.id) !== (initialProjectId || '')) {
          handleProjectChange(String(i.project.id))
        }
      })
      .catch(err => {
        if (!cancelled) setBanner({ kind: 'error', message: err?.message || 'Failed to load issue.' })
      })
    return () => { cancelled = true }
  }, [issueId, initialProjectId, handleProjectChange])

  // Required-field validation across the whole single-page form.
  const validateForm = (): Record<string, string> => {
    const errs: Record<string, string> = {}
    if (!formData.project_id) errs.project_id = 'Pick a project to continue.'
    if (!formData.tracker_id) errs.tracker_id = 'Choose an issue type.'
    if (!formData.subject.trim()) errs.subject = 'Give the issue a short title.'
    return errs
  }

  const enhanceDescription = async () => {
    if (!formData.description.trim() || enhancingDescription) return
    setEnhancingDescription(true)
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return
      const res = await fetch('/api/assistant/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeaders as Record<string, string>) },
        body: JSON.stringify({
          userMessage: `TASK: Enhance the following description. Make it clearer, more professional, and actionable while keeping the original intent. Return ONLY the rewritten description text.\n\n${formData.description}`,
          context: { userId: 'page', currentPage: 'issue-create', action: 'enhance-description' },
          assistantType: 'enhanceDescription',
          source: 'other',
        }),
      })
      const data = await res.json()
      if (data.response) {
        let cleaned = String(data.response).trim()
        cleaned = cleaned.replace(/^(Here is|Here's|I've|I have)\s+(the\s+)?(enhanced|improved|updated|rewritten)\s+(description|version|text)[:.]?\s*/i, '')
        cleaned = cleaned.replace(/^\*\*[^*]+:\*\*\s*$/gm, '').replace(/\n{3,}/g, '\n\n').trim()
        setFormData(prev => ({ ...prev, description: cleaned }))
        setBanner({ kind: 'success', message: 'Description enhanced with AI.' })
      } else {
        setBanner({ kind: 'error', message: data.error || 'Failed to enhance description.' })
      }
    } catch (err: any) {
      setBanner({ kind: 'error', message: err?.message || 'Failed to enhance description.' })
    } finally {
      setEnhancingDescription(false)
    }
  }

  const handleSubmit = async () => {
    const errs = validateForm()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      // Scroll to the top of the form so the first error is in view.
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setSubmitting(true)
    setBanner(null)
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) {
        setBanner({ kind: 'error', message: 'You must be signed in to create an issue.' })
        setSubmitting(false)
        return
      }

      const payload: any = {
        subject: formData.subject.trim(),
        project_id: formData.project_id,
        tracker_id: formData.tracker_id,
      }
      if (formData.description.trim()) payload.description = formData.description.trim()
      if (formData.status_id) payload.status_id = formData.status_id
      if (formData.priority_id) payload.priority_id = formData.priority_id
      if (formData.assigned_to_id) payload.assigned_to_id = formData.assigned_to_id
      if (formData.fixed_version_id) payload.version_id = formData.fixed_version_id
      if (formData.category_id) payload.category_id = formData.category_id
      if (formData.parent_issue_id) payload.parent_id = formData.parent_issue_id
      if (formData.start_date) payload.start_date = formData.start_date
      if (formData.due_date) payload.due_date = formData.due_date
      if (formData.estimated_hours) payload.estimated_hours = parseFloat(formData.estimated_hours)

      if (isEdit) {
        if (formData.notes?.trim()) payload.notes = formData.notes.trim()
        if (formData.is_private) payload.is_private = true
        if (formData.done_ratio) payload.done_ratio = parseInt(formData.done_ratio, 10) || 0
      }

      const res = await fetch(isEdit ? `/api/issues/${issueId}` : '/api/issues', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...(authHeaders as Record<string, string>) },
        body: JSON.stringify({ issue: payload }),
      })

      if (!res.ok) {
        const error = await res.json().catch(() => ({}))
        let msg = error.error || (Array.isArray(error.errors) ? error.errors.join('\n') : error.errors) || `Failed to ${isEdit ? 'update' : 'create'} issue`

        const lc = String(msg).toLowerCase()
        const mapped: Record<string, string> = {}
        if (lc.includes('project')) mapped.project_id = msg
        if (lc.includes('tracker') || lc.includes('type')) mapped.tracker_id = msg
        if (lc.includes('subject') || lc.includes('title')) mapped.subject = msg
        if (Object.keys(mapped).length) {
          setFieldErrors(mapped)
          if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          setBanner({ kind: 'error', message: msg })
        }
        setSubmitting(false)
        return
      }

      const responseJson = await res.json()
      const newIssueId = responseJson.issue?.id || issueId
      if (!newIssueId) {
        setBanner({ kind: 'error', message: `Issue was ${isEdit ? 'updated' : 'created'} but no ID was returned.` })
        setSubmitting(false)
        return
      }

      // Attachments — best-effort, do not block navigation on failure
      let attachmentMessage = ''
      if (pendingFiles.length > 0) {
        try {
          const r = await uploadPendingAttachments(newIssueId, pendingFiles)
          if (r.failed > 0) {
            const details = r.failures.map(f => `${f.name}: ${f.reason}${f.stage ? ` (${f.stage})` : ''}`).join('\n')
            attachmentMessage = `Issue created, but ${r.failed} of ${pendingFiles.length} attachment(s) failed:\n${details}`
          }
        } catch (err: any) {
          attachmentMessage = `Issue created, but uploading attachments failed: ${err?.message || 'unknown error'}`
        }
      }
      if (attachmentMessage) {
        setBanner({ kind: 'error', message: attachmentMessage })
        setSubmitting(false)
        return
      }

      router.push(`/issues/${newIssueId}`)
    } catch (err: any) {
      setBanner({ kind: 'error', message: err?.message || 'Unexpected error. Please try again.' })
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6">
          <Link href="/" className="hover:text-foreground transition-colors flex items-center gap-1">
            <Home className="h-3 w-3" /> Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link href="/issues" className="hover:text-foreground transition-colors">Issues</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{isEdit ? 'Edit issue' : (parentIssueId ? 'Create subtask' : 'New issue')}</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <div className="rounded-lg p-2.5 bg-primary/10 text-primary mt-0.5">
              <FilePlus className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                {isEdit ? 'Edit issue' : (parentIssueId ? 'Create subtask' : 'Create a new issue')}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isEdit ? 'Update the fields, then save your changes.' : 'Walk through a few quick steps to file your issue.'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => router.back()}>
            Cancel
          </Button>
        </div>

        {/* Banner */}
        {banner && (
          <div
            className={cn(
              'mb-4 rounded-md border px-4 py-3 text-sm flex items-start gap-2',
              banner.kind === 'error'
                ? 'border-destructive/30 bg-destructive/10 text-destructive'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
            )}
            role={banner.kind === 'error' ? 'alert' : 'status'}
          >
            <span className="flex-1 whitespace-pre-wrap">{banner.message}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-current opacity-60 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Form */}
        {loadingData && !modalData ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (
          <CreateIssueWizardBody
            submitLabel={isEdit ? 'Save Changes' : 'Create Issue'}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            formData={formData}
            setFormData={setFormData}
            modalData={modalData}
            handleProjectChange={handleProjectChange}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            enhancingDescription={enhancingDescription}
            enhanceDescription={enhanceDescription}
            loading={submitting}
            onCancel={() => router.back()}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  )
}
